import { FC, useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import cn from 'classnames';
import { DrugItem } from 'src/api/types';
import NextImage from 'src/shared-components/NextImage/NextImage';
import { useAnalytics } from 'src/hooks/useAnalytics/useAnalytics';
import { GTAGPrefix } from 'src/hooks/useGTAG';
import DrugCard from '../DrugCard/DrugCard';
import { DrugCardVariant } from '../DrugCard/types';

const darkShevron = 'shared/darkblue-shevron.svg';
const whiteShevron = 'shared/white-shevron.svg';

interface Props {
  data: DrugItem[];
  gtagPrefix: GTAGPrefix;
  className?: string;
  addToCartGAAction?: string;
  clickOnCardGAAction?: string;
}

const DrugsCarousel: FC<Props> = ({
  data = [],
  gtagPrefix,
  className,
  addToCartGAAction,
  clickOnCardGAAction
}) => {
  const [emblaRef, embla] = useEmblaCarousel({
    startIndex: 0,
    dragFree: true,
    draggable: false,
    containScroll: 'keepSnaps'
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const { sendSelectItemAnalytic } = useAnalytics();

  const handleSendAnalytic = (drug: DrugItem) => () => {
    setTimeout(() => {
      sendSelectItemAnalytic(drug);
    });
  };

  const onSelect = useCallback(() => {
    if (embla) {
      setSelectedIndex(embla.selectedScrollSnap());
    }
  }, [embla, setSelectedIndex]);

  useEffect(() => {
    if (embla) {
      onSelect();
      setScrollSnaps(embla.scrollSnapList());
      embla.on('select', onSelect);
    }
  }, [embla, setScrollSnaps, onSelect]);

  const handleRightArrowClick = useCallback(() => {
    embla.scrollTo(selectedIndex + 5);
  }, [embla, scrollSnaps, selectedIndex]);

  const handleLeftArrowClick = useCallback(() => {
    embla.scrollTo(selectedIndex - 5);
  }, [embla, scrollSnaps, selectedIndex]);

  const reachedLeft = selectedIndex === 0;
  const reachedRight = selectedIndex >= scrollSnaps?.length - 1;

  return (
    <div
      className={cn(
        'w-full h-full flex flex pt-[63px] items-center justify-center',
        className
      )}
    >
      <button
        disabled={reachedLeft}
        className={cn(
          'flex items-center justify-center min-w-[36px] compendium-min-h-9 w-9 h-9 rounded-full mr-[9px]',
          {
            'bg-astronautBlue pr-[2px]': reachedLeft,
            'bg-gallery rotate-180 pl-[2px]': !reachedLeft,
            hidden: reachedLeft && reachedRight
          }
        )}
        onClick={handleLeftArrowClick}
      >
        <NextImage
          src={reachedLeft ? whiteShevron : darkShevron}
          layout="fixed"
          height={16}
          width={8}
          alt="arrow-left"
        />
      </button>
      <div
        className="flex w-full h-full overflow-hidden max-w-[1190px]"
        ref={emblaRef}
      >
        <div className="flex w-full h-full">
          {data?.map((drug, index) => {
            return (
              <DrugCard
                key={index}
                drug={drug}
                variant={DrugCardVariant.MinimalisticCard}
                gtagPrefix={gtagPrefix}
                containerClassName="min-w-[238px] max-w-[238px]"
                addToCartGAAction={addToCartGAAction}
                clickOnCardGAAction={clickOnCardGAAction}
                onClick={handleSendAnalytic(drug)}
              />
            );
          })}
        </div>
      </div>
      <button
        disabled={reachedRight}
        className={cn(
          'flex items-center justify-center min-w-[36px] compendium-min-h-9 w-9 h-9 rounded-full ml-[9px]',
          {
            'bg-astronautBlue rotate-180 pr-[2px]': reachedRight,
            'bg-gallery pl-[2px]': !reachedRight,
            hidden: reachedLeft && reachedRight
          }
        )}
        onClick={handleRightArrowClick}
      >
        <NextImage
          src={reachedRight ? whiteShevron : darkShevron}
          layout="fixed"
          height={16}
          width={8}
          alt="arrow-right"
        />
      </button>
    </div>
  );
};

export default DrugsCarousel;
